import { cn } from '@/lib/utils'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { DistributionChart } from '@/frontend/features/Fde/dataHealth/CheckCharts'
import { DailySeriesChart, type DailyMarks } from '@/frontend/features/Fde/dataHealth/GapTimeline'
import { dotClass, metricHelp, metricLabel, statusLabel } from '@/frontend/features/Fde/dataHealth/labels'
import {
  checkWhere, columnOf, describeCheck, notAllowedCodes, num, outlierRange, relationshipOf, type MeasureContext,
} from '@/frontend/features/Fde/dataHealth/measures'
import { ColumnName, TableName } from '@/frontend/features/Fde/dataHealth/Names'
import type { HealthCheck, HealthCopy, HealthRow, HealthSeries, HealthTable } from '@/frontend/features/Fde/dataHealth/types'
import { format, formatDate, formatNumber, formatScalar } from '@/frontend/features/Fde/dataHealth/types'

// Side panel of one check: the fixed sentence, what the measure measures, value against limit, the
// measured detail (chart or lists) and example rows. Only what was measured; no causes, no meaning.

const TH = 'whitespace-nowrap border-b border-border px-2 py-1.5 text-left align-bottom text-[11px] font-semibold text-muted-foreground'
const TD = 'max-w-48 truncate border-b border-border/60 px-2 py-1.5 align-top'

export function PackTag({ copy }: { copy: HealthCopy }) {
  return (
    <span title={copy.source_pack_hint} className="ml-1.5 inline-block rounded border border-border px-1 align-middle text-[10px] font-normal leading-4 text-muted-foreground">
      {copy.source_pack}
    </span>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="grid min-w-0 gap-2">
      <h3 className="m-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      {children}
    </section>
  )
}

// Rows of records: header with dictionary names, cells truncated with the full value on hover.
function RowsTable({ rows, columns, table, copy, extra }: {
  rows: HealthRow[]; columns?: string[] | null; table?: HealthTable; copy: HealthCopy; extra?: Record<string, string>
}) {
  const order = columns?.length ? columns : [...new Set(rows.flatMap((row) => Object.keys(row)))]
  if (!rows.length || !order.length) return null
  return (
    <div className="overflow-x-auto" role="region" tabIndex={0} aria-label={copy.examples}>
      <table className="w-full border-collapse text-xs tabular-nums">
        <thead>
          <tr>
            {order.map((name) => (
              <th key={name} className={TH}>
                {extra?.[name] ?? (columnOf(table, name) ? <ColumnName table={table} name={name} copy={copy} /> : <span className="font-mono">{name}</span>)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              {order.map((name) => {
                const text = formatScalar(row[name])
                return <td key={name} className={TD} title={text}>{text}</td>
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function seriesOf(check: HealthCheck, table?: HealthTable): HealthSeries | null {
  if (check.detail?.series?.counts?.length) return check.detail.series
  // older runs: the table's own daily counts carry the gaps and anomalies (not per partition)
  if (!check.partition && table?.daily_counts?.counts?.length && ['max_gap_days', 'volume_anomaly_days', 'days_since_last'].includes(check.metric)) return table.daily_counts
  return null
}

function nextDay(value: string) {
  const time = Date.parse(`${value.slice(0, 10)}T00:00:00Z`)
  return Number.isNaN(time) ? value : new Date(time + 86_400_000).toISOString().slice(0, 10)
}

function marksOf(check: HealthCheck, table: HealthTable | undefined, series: HealthSeries): DailyMarks {
  const values = check.values ?? {}
  switch (check.metric) {
    case 'max_gap_days': {
      const gaps = check.detail?.gaps?.length ? check.detail.gaps : check.detail?.series ? [] : table?.gaps ?? []
      return { ranges: gaps.map((gap) => ({ from: gap.from, to: gap.to })) }
    }
    case 'volume_anomaly_days':
      return { anomalies: check.detail?.anomalies ?? (check.detail?.series ? [] : table?.volume_anomalies ?? []) }
    case 'days_since_last':
    case 'partition_days_since_last': {
      const last = typeof values.last_date === 'string' ? values.last_date : null
      const end = new Date(Date.parse(`${series.start}T00:00:00Z`) + (series.counts.length - 1) * 86_400_000).toISOString().slice(0, 10)
      return last && last < end ? { ranges: [{ from: nextDay(last), to: end }] } : {}
    }
    case 'snapshot_missing_days':
      return { zeroIsGap: true }
    case 'snapshot_rows_drop_pct':
      return { focus: typeof values.last_date === 'string' ? values.last_date : null, reference: num(values.median_rows) }
    default:
      return {}
  }
}

function NumberList({ label, values }: { label: string; values?: number[] | null }) {
  if (!values?.length) return null
  return <p className="m-0 text-xs"><span className="text-muted-foreground">{label}: </span><span className="tabular-nums">{values.map((value) => formatNumber(value)).join(' · ')}</span></p>
}

// The measured detail: a chart for time and distribution metrics, lists for codes, keys and records without a match.
function Measured({ check, table, ctx, staff }: { check: HealthCheck; table?: HealthTable; ctx: MeasureContext; staff: boolean }) {
  const { copy } = ctx
  const detail = check.detail ?? {}
  const parts: React.ReactNode[] = []
  const series = seriesOf(check, table)
  if (series) parts.push(<DailySeriesChart key="series" series={series} marks={marksOf(check, table, series)} copy={copy} />)

  if (check.metric === 'days_since_last' && detail.partitions?.length) {
    const rows = detail.partitions.map((part) => ({ value: part.value, last_date: part.last_date, days: part.days_since_last }))
    parts.push(<RowsTable key="partitions" rows={rows} columns={['value', 'last_date', 'days']} copy={copy} extra={{ value: table?.partition_column ?? copy.partition, last_date: copy.col_last_date, days: copy.col_days_without }} />)
  }

  if (check.metric === 'outlier_pct') {
    const { low, high } = outlierRange(check, table)
    if (detail.histogram?.length) parts.push(<DistributionChart key="histogram" histogram={detail.histogram} low={low} high={high} copy={copy} />)
    else if (low != null && high != null) parts.push(<p key="range" className="m-0 text-xs">{format(copy.chart_common_range, { low: formatNumber(low), high: formatNumber(high) })}</p>)
    if (detail.q1 != null && detail.q3 != null) parts.push(<p key="q" className="m-0 text-xs"><span className="text-muted-foreground">{copy.detail_quartiles}: </span><span className="tabular-nums">{format(copy.range, { from: formatNumber(detail.q1), to: formatNumber(detail.q3) })}</span></p>)
    if (detail.top?.length) parts.push(<NumberList key="top" label={copy.detail_top} values={detail.top} />)
    if (detail.bottom?.length) parts.push(<NumberList key="bottom" label={copy.detail_bottom} values={detail.bottom} />)
  }

  if (check.metric === 'date_out_of_range' && (detail.before != null || detail.after != null)) {
    const min = detail.min_day ?? (typeof check.values?.min_day === 'string' ? check.values.min_day : null)
    const max = detail.max_day ?? (typeof check.values?.max_day === 'string' ? check.values.max_day : null)
    parts.push(
      <ul key="range" className="m-0 grid list-none gap-0.5 p-0 text-xs tabular-nums">
        {detail.before != null && <li>{format(copy.detail_before, { date: min ? formatDate(min) : '-', count: formatNumber(detail.before) })}</li>}
        {detail.after != null && <li>{format(copy.detail_after, { date: max ? formatDate(max) : '-', count: formatNumber(detail.after) })}</li>}
      </ul>,
    )
  }

  if (check.metric === 'value_not_allowed_pct') {
    const codes = notAllowedCodes(check)
    if (codes.length) parts.push(<RowsTable key="codes" rows={codes.map((code) => ({ value: code.value, rows: code.rows }))} columns={['value', 'rows']} copy={copy} extra={{ value: copy.detail_code, rows: copy.detail_rows }} />)
    const allowed = detail.allowed?.length ? detail.allowed : typeof check.values?.allowed === 'string' ? check.values.allowed.split('|').filter(Boolean) : []
    if (allowed.length) parts.push(<p key="allowed" className="m-0 break-words text-xs"><span className="text-muted-foreground">{copy.detail_allowed}: </span><span className="font-mono">{allowed.map((code) => (code === '' ? copy.empty_code : code)).join(', ')}</span></p>)
  }

  if (check.metric === 'duplicate_keys' && detail.keys?.length) {
    const keyColumns = [...new Set(detail.keys.flatMap((row) => Object.keys(row)))].filter((name) => name !== 'copies')
    parts.push(<RowsTable key="keys" rows={detail.keys} columns={[...keyColumns, 'copies']} table={table} copy={copy} extra={{ copies: copy.detail_copies }} />)
  }

  if (check.metric === 'match_pct') {
    const unmatched = detail.unmatched?.length ? detail.unmatched : staff ? relationshipOf(check, ctx).relationship?.examples ?? [] : []
    if (unmatched.length) parts.push(<div key="unmatched" className="grid gap-1"><p className="m-0 text-xs text-muted-foreground">{copy.detail_unmatched}</p><RowsTable rows={unmatched} table={table} copy={copy} /></div>)
  }

  return parts.length ? <Section title={copy.panel_measured_detail}>{parts}</Section> : null
}

function examplesOf(check: HealthCheck, table: HealthTable | undefined, staff: boolean): HealthRow[] {
  if (check.examples?.length) return check.examples
  if (!staff) return []
  // older runs kept a few raw rows for the data team only
  if (check.metric === 'duplicate_keys') return table?.duplicate_keys?.examples ?? []
  if (check.metric === 'outlier_pct' && check.column) return columnOf(table, check.column)?.outliers?.examples ?? []
  return []
}

function PanelBody({ check, ctx, staff }: { check: HealthCheck; ctx: MeasureContext; staff: boolean }) {
  const { copy } = ctx
  const table = ctx.byId.get(check.table)
  const description = describeCheck(check, ctx)
  const help = metricHelp(check.metric, copy)
  const where = checkWhere(check, table, copy)
  const examples = examplesOf(check, table, staff)
  return (
    <>
      <header className="grid gap-1.5 pr-8">
        <p className="m-0 flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className={cn('inline-block size-2.5 shrink-0 rounded-full', dotClass[check.status])} aria-hidden />
          {statusLabel(check.status, copy)}
          {check.source === 'pack' && <PackTag copy={copy} />}
        </p>
        <DialogTitle className="text-lg leading-snug">{metricLabel(check.metric, copy)}</DialogTitle>
        <DialogDescription asChild>
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm text-foreground">
            <TableName id={check.table} table={table} copy={copy} />
            {where.map((item) => (
              <span key={`${item.kind}-${item.name}`} className="text-muted-foreground">
                {'· '}{item.kind === 'column' ? <ColumnName table={table} name={item.name} copy={copy} /> : item.name}
              </span>
            ))}
          </div>
        </DialogDescription>
      </header>

      <p className="m-0 text-[15px] leading-relaxed">{description.sentence}</p>

      <dl className="m-0 grid grid-cols-2 gap-3 border-y border-border py-3">
        <div className="min-w-0">
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{copy.col_problem}</dt>
          <dd className="m-0 mt-0.5 break-words text-sm font-semibold tabular-nums">{description.problem}</dd>
        </div>
        <div className="min-w-0">
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{copy.col_limit}</dt>
          <dd className="m-0 mt-0.5 break-words text-sm tabular-nums">{description.limit ?? copy.limit_none}</dd>
        </div>
      </dl>

      {help && <Section title={copy.panel_what}><p className="m-0 text-sm">{help}</p></Section>}

      <Measured check={check} table={table} ctx={ctx} staff={staff} />

      {examples.length > 0 && (
        <Section title={format(copy.panel_examples, { count: examples.length })}>
          <RowsTable rows={examples} columns={check.example_columns} table={table} copy={copy} />
        </Section>
      )}
    </>
  )
}

export default function CheckPanel({ check, ctx, staff, onClose }: { check: HealthCheck | null; ctx: MeasureContext; staff: boolean; onClose: () => void }) {
  return (
    <Dialog open={!!check} onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent
        className="left-auto right-0 top-0 h-dvh max-h-dvh w-full max-w-xl translate-x-0 translate-y-0 content-start gap-5 overflow-y-auto overscroll-contain rounded-none border-y-0 border-r-0 p-5 sm:max-w-xl sm:rounded-none sm:p-6"
      >
        {check && <PanelBody check={check} ctx={ctx} staff={staff} />}
      </DialogContent>
    </Dialog>
  )
}
